import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, RefreshCw, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const Row = ({ ok, children }) => (
  <li className="flex items-start gap-2 text-sm">
    {ok ? <CheckCircle2 size={14} className="text-emerald-700 mt-0.5 shrink-0" /> : <XCircle size={14} className="text-amber-700 mt-0.5 shrink-0" />}
    <span className={ok ? "text-slate-700" : "text-amber-900"}>{children}</span>
  </li>
);

export default function TraegerReadiness({ formId, beforeCheck }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    setBusy(true);
    try {
      await beforeCheck?.();
      const r = await api.get(`/traeger-forms/form/${formId}/readiness`, { timeout: 120000 });
      setData(r.data);
    } catch {
      setData(null);
    } finally { setBusy(false); }
  }, [formId]); // eslint-disable-line

  useEffect(() => { check(); }, [formId]); // eslint-disable-line

  if (!data && busy) return <div className="flex items-center gap-2 text-xs text-slate-500 px-4 py-2.5 border border-slate-200" data-testid="traeger-readiness"><Loader2 size={13} className="animate-spin" /> Versandfertigkeit wird geprüft …</div>;
  if (!data) return null;

  const ok = data.ok;
  const tone = ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50";
  return (
    <div className={`border ${tone} p-4 space-y-2`} data-testid="traeger-readiness">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className={`flex items-center gap-2 text-sm font-medium ${ok ? "text-emerald-900" : "text-amber-900"}`} data-testid="traeger-readiness-status">
          {ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {ok ? "Versandfertig – Datum, Stempel und Unterschrift sitzen auf allen Unterschriftsseiten" : "Noch nicht versandfertig"}
        </div>
        <button onClick={check} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50" data-testid="traeger-readiness-recheck-btn">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Erneut prüfen
        </button>
      </div>
      {!ok && (
        <ul className="space-y-1" data-testid="traeger-readiness-items">
          {!data.signature_available && <Row ok={false}>Es ist keine Unterschrift hinterlegt – oben im Trägerprofil hochladen</Row>}
          {!data.stamp_available && <Row ok={false}>Es ist kein Firmenstempel hinterlegt – oben im Trägerprofil hochladen</Row>}
          {data.pages.length === 0 && <Row ok={false}>Auf den ausgewählten Seiten wurde keine Unterschriftsfläche erkannt</Row>}
          {data.pages.map((p) => (
            <Row key={p.page} ok={p.signature && p.stamp && p.date}>
              Seite {p.page}: Datum {p.date ? "✓" : "fehlt"} · Unterschrift {p.signature ? "✓" : "fehlt"} · Stempel {p.stamp ? "✓" : "fehlt"}
            </Row>
          ))}
          {data.missing_count > 0 && (
            <Row ok={false}>
              {data.missing_count} Angabe{data.missing_count === 1 ? "" : "n"} noch leer: {data.missing.slice(0, 6).join(", ")}{data.missing_count > 6 ? " …" : ""}
            </Row>
          )}
        </ul>
      )}
    </div>
  );
}
